import React, { useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import { Print as PrintIcon, Close as CloseIcon } from "@mui/icons-material";
import arLogo from "../../assets/ar-infotech-solutions.png";

export const PURPLE = "#8E90EB";
export const PURPLE_BG = "#8E90EB";
export const PURPLE_LIGHT = "#9484E8";
export const BORDER_COLOR = "#777777";
export const BORDER_DARK = "#777777";
export const TEXT_DARK = "#000000";
export const TEXT_BLACK = "#000000";
export const TEXT_MUTED = "#555566";

// Number to Words Converter for Indian Rupee Currency
export function convertNumberToWords(num) {
  const ones = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
    "Seventeen", "Eighteen", "Nineteen",
  ];
  const tens = [
    "", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety",
  ];

  if (!num || isNaN(num)) return "Zero Rupees only";
  const n = Math.round(parseFloat(num));
  if (n === 0) return "Zero Rupees only";

  const inWords = (val) => {
    if (val < 20) return ones[val];
    if (val < 100) return tens[Math.floor(val / 10)] + (val % 10 ? " " + ones[val % 10] : "");
    return ones[Math.floor(val / 100)] + " Hundred" + (val % 100 ? " " + inWords(val % 100) : "");
  };

  let str = "";
  if (Math.floor(n / 10000000)) str += inWords(Math.floor(n / 10000000)) + " Crore ";
  if (Math.floor((n % 10000000) / 100000)) str += inWords(Math.floor((n % 10000000) / 100000)) + " Lakh ";
  if (Math.floor((n % 100000) / 1000)) str += inWords(Math.floor((n % 100000) / 1000)) + " Thousand ";
  if (n % 1000) str += inWords(n % 1000);
  return str.trim() + " Rupees only";
}

export const formatCurrency = (val) =>
  new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(parseFloat(val) || 0);

export const formatDate = (d) => {
  if (!d) return "14-09-2026";
  try {
    const dateObj = new Date(d);
    if (isNaN(dateObj.getTime())) return d;
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return d;
  }
};

/**
 * Universal Invoice Document Component
 * Exact 1:1 match with Image 1 specification.
 */
export const InvoiceDocument = ({ data, invoiceRef }) => {
  if (!data) return null;

  // Normalize data structure
  const customerName = data.customer_name || data.customerName || data.name || "Vijayakumar G";
  const customerPhone = data.customer_number || data.customerNumber || data.customerPhone || data.phone || "9176658923";
  const invoiceNumber = data.invoice_number || data.invoiceNumber || (data.id ? `AR-IN-${data.id.toString().replace(/[^0-9]/g, '') || '455916'}` : "AR-IN-455916");
  const invoiceDate = formatDate(data.due_date || data.dueDate || data.created_at || data.date);

  // Normalize items array
  const rawItems = (data.items && data.items.length > 0) ? data.items : [
    { description: "Microsoft office 2024 pro plus", hsn_sac: "997331", quantity: 1, unit: "Pcs", unit_price: 600, total_price: 600 }
  ];

  const items = rawItems.map((item, index) => {
    const qty = parseFloat(item.quantity || item.qty || 1);
    const unitPrice = parseFloat(
      item.unit_price || item.unitPrice || item.price?.toString().replace(/[^0-9.]/g, "") || 0
    );
    const totalPrice = parseFloat(
      item.total_price || item.totalPrice || item.total?.toString().replace(/[^0-9.]/g, "") || (qty * unitPrice)
    );
    return {
      index: index + 1,
      description: item.description || item.name || item.product_name || "Microsoft office 2024 pro plus",
      hsn_sac: item.hsn_sac || item.hsn || "997331",
      quantity: qty,
      unit: item.unit || "Pcs",
      unit_price: unitPrice,
      total_price: totalPrice,
    };
  });

  const subTotal = items.reduce((sum, i) => sum + (parseFloat(i.total_price) || 0), 0);
  const total = parseFloat(
    data.total || data.total_amount || data.totalAmount?.toString().replace(/[^0-9.]/g, "") || subTotal
  );
  const received = data.received !== undefined ? parseFloat(data.received) : total;
  const balance = Math.max(0, total - received);
  const totalQty = items.reduce((sum, i) => sum + (parseFloat(i.quantity) || 0), 0);
  const amountInWords = data.amountInWords || convertNumberToWords(total);

  return (
    <Box
      ref={invoiceRef}
      className="invoice-printable-container"
      sx={{
        width: "100%",
        maxWidth: "850px",
        margin: "0 auto",
        backgroundColor: "#FFFFFF",
        fontFamily: "Arial, Helvetica, sans-serif",
        color: TEXT_BLACK,
        p: 2,
        boxSizing: "border-box",
      }}
    >
      {/* ── 1. Top Centered Title ── */}
      <Box sx={{ textAlign: "center", mb: 1 }}>
        <Typography sx={{ fontWeight: "bold", fontSize: "16px", color: "#000000", letterSpacing: "0.2px" }}>
          Invoice
        </Typography>
      </Box>

      {/* ── 2. Main Border Box ── */}
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          border: `1.5px solid ${BORDER_DARK}`,
          backgroundColor: "#FFFFFF",
          fontSize: "12px",
          color: "#000000",
        }}
      >
        <tbody>
          {/* ── Company Info Header ── */}
          <tr>
            <td
              style={{
                width: "22%",
                borderRight: `1px solid ${BORDER_DARK}`,
                borderBottom: `1px solid ${BORDER_DARK}`,
                padding: "12px 10px",
                textAlign: "center",
                verticalAlign: "middle",
              }}
            >
              <img
                src={arLogo}
                alt="AR INFOTECH SOLUTIONS"
                style={{ maxWidth: "120px", maxHeight: "70px", objectFit: "contain" }}
              />
            </td>
            <td
              style={{
                width: "78%",
                borderBottom: `1px solid ${BORDER_DARK}`,
                padding: "10px 14px",
                textAlign: "right",
                verticalAlign: "middle",
                lineHeight: 1.45,
              }}
            >
              <div style={{ fontSize: "16px", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                AR INFOTECH SOLUTIONS
              </div>
              <div style={{ fontSize: "11px", marginTop: "3px" }}>
                Unit 101, Oxford Towers, 139/88, Hal Old Airport Rd Kodihalli, Bangalore, Karnataka, India,
              </div>
              <div style={{ fontSize: "11px" }}>
                Phone no.: 9901991771 Email: arinfotechsolution11@gmail.com
              </div>
              <div style={{ fontSize: "11px", fontWeight: "bold" }}>
                GSTIN: 29BTWPA6587J1ZQ, State: 29-Karnataka
              </div>
            </td>
          </tr>

          {/* ── Bill To & Invoice Details Header ── */}
          <tr>
            <td
              style={{
                width: "50%",
                backgroundColor: PURPLE_BG,
                color: "#FFFFFF",
                fontWeight: "bold",
                padding: "5px 10px",
                borderRight: `1px solid ${BORDER_DARK}`,
                borderBottom: `1px solid ${BORDER_DARK}`,
                fontSize: "12px",
              }}
            >
              Bill To
            </td>
            <td
              style={{
                width: "50%",
                backgroundColor: PURPLE_BG,
                color: "#FFFFFF",
                fontWeight: "bold",
                padding: "5px 10px",
                textAlign: "right",
                borderBottom: `1px solid ${BORDER_DARK}`,
                fontSize: "12px",
              }}
            >
              Invoice Details
            </td>
          </tr>

          {/* ── Bill To & Invoice Details Content ── */}
          <tr>
            <td
              style={{
                width: "50%",
                borderRight: `1px solid ${BORDER_DARK}`,
                borderBottom: `1px solid ${BORDER_DARK}`,
                padding: "8px 10px",
                verticalAlign: "top",
                lineHeight: 1.6,
              }}
            >
              <div style={{ fontWeight: "bold", fontSize: "13px" }}>{customerName}</div>
              <div style={{ fontSize: "11.5px" }}>Contact No.: {customerPhone}</div>
            </td>
            <td
              style={{
                width: "50%",
                borderBottom: `1px solid ${BORDER_DARK}`,
                padding: "8px 10px",
                textAlign: "right",
                verticalAlign: "top",
                lineHeight: 1.6,
              }}
            >
              <div style={{ fontSize: "11.5px" }}>Invoice No.: {invoiceNumber}</div>
              <div style={{ fontSize: "11.5px" }}>Date: {invoiceDate}</div>
            </td>
          </tr>

          {/* ── Items Table ── */}
          <tr>
            <td colSpan={2} style={{ padding: 0, borderBottom: `1px solid ${BORDER_DARK}` }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11.5px" }}>
                <thead>
                  <tr style={{ backgroundColor: PURPLE_BG, color: "#FFFFFF", fontWeight: "bold" }}>
                    <th
                      style={{
                        width: "5%",
                        padding: "5px 4px",
                        borderRight: `1px solid ${BORDER_DARK}`,
                        borderBottom: `1px solid ${BORDER_DARK}`,
                        textAlign: "center",
                      }}
                    >
                      #
                    </th>
                    <th
                      style={{
                        width: "36%",
                        padding: "5px 8px",
                        borderRight: `1px solid ${BORDER_DARK}`,
                        borderBottom: `1px solid ${BORDER_DARK}`,
                        textAlign: "left",
                      }}
                    >
                      Item Name
                    </th>
                    <th
                      style={{
                        width: "14%",
                        padding: "5px 6px",
                        borderRight: `1px solid ${BORDER_DARK}`,
                        borderBottom: `1px solid ${BORDER_DARK}`,
                        textAlign: "center",
                      }}
                    >
                      HSN/ SAC
                    </th>
                    <th
                      style={{
                        width: "12%",
                        padding: "5px 6px",
                        borderRight: `1px solid ${BORDER_DARK}`,
                        borderBottom: `1px solid ${BORDER_DARK}`,
                        textAlign: "center",
                      }}
                    >
                      Quantity
                    </th>
                    <th
                      style={{
                        width: "9%",
                        padding: "5px 6px",
                        borderRight: `1px solid ${BORDER_DARK}`,
                        borderBottom: `1px solid ${BORDER_DARK}`,
                        textAlign: "center",
                      }}
                    >
                      Unit
                    </th>
                    <th
                      style={{
                        width: "12%",
                        padding: "5px 8px",
                        borderRight: `1px solid ${BORDER_DARK}`,
                        borderBottom: `1px solid ${BORDER_DARK}`,
                        textAlign: "right",
                      }}
                    >
                      Price/ Unit
                    </th>
                    <th
                      style={{
                        width: "12%",
                        padding: "5px 8px",
                        borderBottom: `1px solid ${BORDER_DARK}`,
                        textAlign: "right",
                      }}
                    >
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => (
                    <tr key={idx}>
                      <td
                        style={{
                          padding: "6px 4px",
                          borderRight: `1px solid ${BORDER_DARK}`,
                          borderBottom: `1px solid ${BORDER_DARK}`,
                          textAlign: "center",
                        }}
                      >
                        {item.index}
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          borderRight: `1px solid ${BORDER_DARK}`,
                          borderBottom: `1px solid ${BORDER_DARK}`,
                          fontWeight: "bold",
                        }}
                      >
                        {item.description}
                      </td>
                      <td
                        style={{
                          padding: "6px 6px",
                          borderRight: `1px solid ${BORDER_DARK}`,
                          borderBottom: `1px solid ${BORDER_DARK}`,
                          textAlign: "center",
                        }}
                      >
                        {item.hsn_sac}
                      </td>
                      <td
                        style={{
                          padding: "6px 6px",
                          borderRight: `1px solid ${BORDER_DARK}`,
                          borderBottom: `1px solid ${BORDER_DARK}`,
                          textAlign: "center",
                        }}
                      >
                        {item.quantity}
                      </td>
                      <td
                        style={{
                          padding: "6px 6px",
                          borderRight: `1px solid ${BORDER_DARK}`,
                          borderBottom: `1px solid ${BORDER_DARK}`,
                          textAlign: "center",
                        }}
                      >
                        {item.unit}
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          borderRight: `1px solid ${BORDER_DARK}`,
                          borderBottom: `1px solid ${BORDER_DARK}`,
                          textAlign: "right",
                        }}
                      >
                        ₹ {formatCurrency(item.unit_price)}
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          borderBottom: `1px solid ${BORDER_DARK}`,
                          textAlign: "right",
                          fontWeight: "bold",
                        }}
                      >
                        ₹ {formatCurrency(item.total_price)}
                      </td>
                    </tr>
                  ))}
                  {/* Total Row */}
                  <tr>
                    <td
                      style={{
                        padding: "6px 4px",
                        borderRight: `1px solid ${BORDER_DARK}`,
                        borderBottom: `1px solid ${BORDER_DARK}`,
                      }}
                    />
                    <td
                      style={{
                        padding: "6px 8px",
                        borderRight: `1px solid ${BORDER_DARK}`,
                        borderBottom: `1px solid ${BORDER_DARK}`,
                        fontWeight: "bold",
                      }}
                    >
                      Total
                    </td>
                    <td
                      style={{
                        padding: "6px 6px",
                        borderRight: `1px solid ${BORDER_DARK}`,
                        borderBottom: `1px solid ${BORDER_DARK}`,
                      }}
                    />
                    <td
                      style={{
                        padding: "6px 6px",
                        borderRight: `1px solid ${BORDER_DARK}`,
                        borderBottom: `1px solid ${BORDER_DARK}`,
                        textAlign: "center",
                        fontWeight: "bold",
                      }}
                    >
                      {totalQty}
                    </td>
                    <td
                      style={{
                        padding: "6px 6px",
                        borderRight: `1px solid ${BORDER_DARK}`,
                        borderBottom: `1px solid ${BORDER_DARK}`,
                      }}
                    />
                    <td
                      style={{
                        padding: "6px 8px",
                        borderRight: `1px solid ${BORDER_DARK}`,
                        borderBottom: `1px solid ${BORDER_DARK}`,
                      }}
                    />
                    <td
                      style={{
                        padding: "6px 8px",
                        borderBottom: `1px solid ${BORDER_DARK}`,
                        textAlign: "right",
                        fontWeight: "bold",
                      }}
                    >
                      ₹ {formatCurrency(subTotal)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>

          {/* ── Middle Split Section ── */}
          <tr>
            <td
              style={{
                width: "50%",
                padding: 0,
                borderRight: `1px solid ${BORDER_DARK}`,
                borderBottom: `1px solid ${BORDER_DARK}`,
                verticalAlign: "bottom",
              }}
            >
              {/* Empty upper area */}
              <div style={{ minHeight: "95px", borderBottom: `1px solid ${BORDER_DARK}` }} />
              {/* Invoice Amount in Words Header */}
              <div
                style={{
                  backgroundColor: PURPLE_BG,
                  color: "#FFFFFF",
                  fontWeight: "bold",
                  padding: "5px 10px",
                  fontSize: "12px",
                  borderBottom: `1px solid ${BORDER_DARK}`,
                }}
              >
                Invoice Amount In Words
              </div>
              {/* Amount in words value */}
              <div
                style={{
                  padding: "8px 10px",
                  textAlign: "center",
                  fontSize: "11.5px",
                  fontWeight: "normal",
                  color: "#000000",
                }}
              >
                {amountInWords}
              </div>
            </td>

            <td
              style={{
                width: "50%",
                padding: 0,
                borderBottom: `1px solid ${BORDER_DARK}`,
                verticalAlign: "top",
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11.5px" }}>
                <thead>
                  <tr style={{ backgroundColor: PURPLE_BG, color: "#FFFFFF", fontWeight: "bold" }}>
                    <th
                      colSpan={2}
                      style={{
                        padding: "5px 10px",
                        textAlign: "left",
                        borderBottom: `1px solid ${BORDER_DARK}`,
                        fontSize: "12px",
                      }}
                    >
                      Amounts
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td
                      style={{
                        padding: "5px 10px",
                        borderRight: `1px solid ${BORDER_DARK}`,
                        borderBottom: `1px solid ${BORDER_DARK}`,
                        width: "60%",
                      }}
                    >
                      Sub Total
                    </td>
                    <td style={{ padding: "5px 10px", borderBottom: `1px solid ${BORDER_DARK}`, textAlign: "right" }}>
                      ₹ {formatCurrency(subTotal)}
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{
                        padding: "5px 10px",
                        borderRight: `1px solid ${BORDER_DARK}`,
                        borderBottom: `1px solid ${BORDER_DARK}`,
                        fontWeight: "bold",
                      }}
                    >
                      Total
                    </td>
                    <td
                      style={{
                        padding: "5px 10px",
                        borderBottom: `1px solid ${BORDER_DARK}`,
                        textAlign: "right",
                        fontWeight: "bold",
                      }}
                    >
                      ₹ {formatCurrency(total)}
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{
                        padding: "5px 10px",
                        borderRight: `1px solid ${BORDER_DARK}`,
                        borderBottom: `1px solid ${BORDER_DARK}`,
                      }}
                    >
                      Received
                    </td>
                    <td style={{ padding: "5px 10px", borderBottom: `1px solid ${BORDER_DARK}`, textAlign: "right" }}>
                      ₹ {formatCurrency(received)}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: "5px 10px", borderRight: `1px solid ${BORDER_DARK}` }}>Balance</td>
                    <td style={{ padding: "5px 10px", textAlign: "right" }}>₹ {formatCurrency(balance)}</td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>

          {/* ── Bottom 3 Columns ── */}
          <tr>
            <td colSpan={2} style={{ padding: 0 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px" }}>
                <tbody>
                  <tr>
                    {/* Column 1: Bank Details */}
                    <td
                      style={{
                        width: "35%",
                        padding: 0,
                        borderRight: `1px solid ${BORDER_DARK}`,
                        verticalAlign: "top",
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: PURPLE_BG,
                          color: "#FFFFFF",
                          fontWeight: "bold",
                          padding: "5px 10px",
                          fontSize: "12px",
                          borderBottom: `1px solid ${BORDER_DARK}`,
                        }}
                      >
                        Bank Details
                      </div>
                      <div style={{ padding: "8px 10px", lineHeight: 1.6, color: "#000000" }}>
                        <div>
                          <strong>Name:</strong> State Bank Of India, Rbi Layout, Bangalore
                        </div>
                        <div>
                          <strong>Account No.:</strong> 45375033092
                        </div>
                        <div>
                          <strong>IFSC code:</strong> SBIN0004408
                        </div>
                        <div>
                          <strong>Account Holder's Name:</strong> AR INFOTECH SOLUTIONS
                        </div>
                      </div>
                    </td>

                    {/* Column 2: Terms and conditions */}
                    <td
                      style={{
                        width: "38%",
                        padding: 0,
                        borderRight: `1px solid ${BORDER_DARK}`,
                        verticalAlign: "top",
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: PURPLE_BG,
                          color: "#FFFFFF",
                          fontWeight: "bold",
                          padding: "5px 10px",
                          fontSize: "12px",
                          borderBottom: `1px solid ${BORDER_DARK}`,
                        }}
                      >
                        Terms and conditions
                      </div>
                      <div style={{ padding: "8px 10px", lineHeight: 1.45, fontSize: "10.5px", color: "#000000" }}>
                        <div style={{ fontWeight: "bold", marginBottom: "2px" }}>Notes:</div>
                        <div>Thank you for shopping with us!</div>
                        <div style={{ marginTop: "4px" }}>1. After 100% payment the product will be delivered.</div>
                        <div>2. After purchasing the payment can't be reversed.</div>
                        <div>3. All the payments will accept UPI, bank transfer.</div>
                        <div>4. Goods once sold will not be taken back.</div>
                        <div>5. No warranty for physically damaged goods.</div>
                        <div style={{ marginTop: "8px", fontStyle: "normal" }}>
                          Declaration: we declare that this invoice shows the actual price of the goods/services
                          described and that all the particulars are true and correct
                        </div>
                      </div>
                    </td>

                    {/* Column 3: Authorized Signatory */}
                    <td
                      style={{
                        width: "27%",
                        padding: "10px",
                        verticalAlign: "top",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          height: "100%",
                          minHeight: "155px",
                        }}
                      >
                        <div style={{ textAlign: "right", fontWeight: "bold", fontSize: "11px", color: "#000000" }}>
                          For: AR INFOTECH SOLUTIONS
                        </div>
                        <div style={{ textAlign: "center", margin: "10px 0" }}>
                          <svg width="120" height="42" viewBox="0 0 140 45" style={{ display: "block", margin: "0 auto" }}>
                            <path
                              d="M 12 30 C 22 8, 32 12, 38 28 C 42 12, 58 8, 62 24 C 68 28, 78 22, 82 28 C 88 18, 102 12, 112 24 C 118 28, 128 30, 132 32"
                              fill="none"
                              stroke="#1d4ed8"
                              strokeWidth="2.2"
                              strokeLinecap="round"
                            />
                            <text
                              x="32"
                              y="28"
                              fontFamily="'Brush Script MT', 'Dancing Script', 'Caveat', cursive"
                              fontSize="26"
                              fill="#1d4ed8"
                              fontWeight="bold"
                            >
                              Avinash
                            </text>
                            <line x1="25" y1="36" x2="115" y2="36" stroke="#1d4ed8" strokeWidth="1.2" strokeDasharray="4 2" />
                          </svg>
                        </div>
                        <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "11px", color: "#000000" }}>
                          Authorized Signatory
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    </Box>
  );
};

/**
 * Printable Helper Function: Opens print dialog with exact A4 print styles
 */
export const printInvoiceElement = (invoiceEl, title = "Invoice") => {
  if (!invoiceEl) return;
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    alert("Please allow popups to print/download invoice.");
    return;
  }

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${title}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          @page {
            size: A4 portrait;
            margin: 8mm;
          }
          body {
            font-family: Arial, Helvetica, sans-serif;
            background-color: #ffffff;
            color: #000000;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          table {
            border-collapse: collapse;
            width: 100%;
          }
          td, th {
            border-color: #777777 !important;
          }
          .invoice-printable-container {
            max-width: 100% !important;
            box-shadow: none !important;
            padding: 0 !important;
          }
          @media print {
            body {
              margin: 0;
              padding: 0;
            }
          }
        </style>
      </head>
      <body>
        ${invoiceEl.innerHTML}
      </body>
    </html>
  `);

  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 450);
};

/**
 * Universal Invoice Modal Popup for easy embedding in user account, checkout, and admin
 */
export const InvoiceModal = ({ open, onClose, data }) => {
  const printRef = useRef(null);

  if (!open || !data) return null;

  const invoiceNo = data.invoice_number || data.invoiceNumber || data.id || "Invoice";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
          backgroundColor: "#F4F5F9",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          py: 1.5,
          backgroundColor: "#FFFFFF",
          borderBottom: `1px solid ${BORDER_DARK}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: "#1A237E", fontSize: "16px" }}>
            Tax Invoice: {invoiceNo}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<PrintIcon />}
            onClick={() => printInvoiceElement(printRef.current, `Invoice-${invoiceNo}`)}
            sx={{
              backgroundColor: PURPLE_BG,
              "&:hover": { backgroundColor: "#787ad8" },
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 1.5,
              px: 2,
            }}
          >
            Download / Print PDF
          </Button>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ p: { xs: 1, sm: 3 }, display: "flex", justifyContent: "center" }}>
        <InvoiceDocument data={data} invoiceRef={printRef} />
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceDocument;
